import { useCallback, useRef } from "react";
import { DocumentApi } from "~/functions/api/document";

interface UseStructuredDocumentApplyOptions {
  acquireLock: () => Promise<unknown>;
  releaseLock: () => Promise<unknown>;
  fetchDocument: () => Promise<unknown>;
  organizationId: string;
  projectId: string;
  documentId: string;
  setIsDiffSaving: (saving: boolean) => void;
  logPrefix: string;
}

/**
 * 構造化ドキュメントの差分適用ロジックを共通化するカスタムフック。
 * ロック取得 → API保存 → ロック解放 → 再フェッチのフローを管理する。
 */
export function useStructuredDocumentApply(
  options: UseStructuredDocumentApplyOptions,
): (updatedContent: string) => Promise<boolean> {
  // 最新のオプションを常に参照するためにrefを使用（stale closureを防止）
  const optionsRef = useRef(options);
  optionsRef.current = options;

  return useCallback(async (updatedContent: string): Promise<boolean> => {
    const {
      acquireLock,
      releaseLock,
      fetchDocument,
      organizationId,
      projectId,
      documentId,
      setIsDiffSaving,
      logPrefix,
    } = optionsRef.current;

    setIsDiffSaving(true);
    let lockAcquired = false;
    try {
      await acquireLock();
      lockAcquired = true;
      const documentApi = new DocumentApi();
      const response = await documentApi.updateContent(organizationId, projectId, documentId, {
        content: updatedContent,
      });
      if (response === null) {
        console.error(`[${logPrefix}] Failed to save structured document content`);
        await releaseLock();
        lockAcquired = false;
        return false;
      }
      await releaseLock();
      lockAcquired = false;
      await fetchDocument();
      return true;
    } catch (error) {
      console.error(`[${logPrefix}] Structured document apply failed:`, error);
      if (lockAcquired) {
        await releaseLock().catch(() => {});
      }
      return false;
    } finally {
      setIsDiffSaving(false);
    }
  }, []); // optionsRefを通じて常に最新値を参照するため依存なし
}
